import { memo, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { cn, formatCurrency } from "@/lib/utils";
import { HeaderCell } from "@/components/header-cell";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { toast } from "sonner";

type BaseAsset = {
  code: string;
  precision: number;
};

type QuoteAsset = {
  code: string;
  precision: number;
};

export type OpenOrder = {
  id: string; // Order ID needed for cancellation
  productId: string;
  symbol: string;
  quote: QuoteAsset;
  base: BaseAsset;
  side: "long" | "short" | "buy" | "sell";
  type: string;
  currentPrice: number;
  limitPrice?: number;
  quantity: number;
  progressPercent: number;
  submittedAt?: string; // Optional - not available in store orders
  takeProfit?: number; // Take profit price
  stopLoss?: number; // Stop loss price
  /** Original USD value when user placed order in USD mode (preserves exact value like $5,000) */
  notionalValue?: number;
};

type OpenOrdersProps = {
  data: OpenOrder[];
  isLoading: boolean;
  onRowClick?: (row: OpenOrder) => void;
  onEditOrder?: (order: OpenOrder) => void;
  editingOrderId?: string | null; // Order being edited (disabled)
  sorting: SortingState;
  onSort: (sorting: SortingState) => void;
  onCancelOrder?: (orderId: string) => void;
  isCancelling?: string | null; // Order ID being cancelled
  onRemoveTakeProfit?: (order: OpenOrder) => void;
  onRemoveStopLoss?: (order: OpenOrder) => void;
};

export const OpenOrders = memo(function OpenOrders({
  data,
  isLoading,
  onRowClick,
  onEditOrder,
  editingOrderId,
  sorting,
  onSort,
  onCancelOrder,
  isCancelling,
  onRemoveTakeProfit,
  onRemoveStopLoss,
}: OpenOrdersProps) {
  const columns = useMemo<ColumnDef<OpenOrder>[]>(
    () => [
      {
        id: "symbol",
        accessorKey: "symbol",
        header: (props) => <HeaderCell header={props}>Symbol</HeaderCell>,
        cell: ({ row }) => {
          const handleSymbolClick = (e: React.MouseEvent) => {
            e.stopPropagation(); // Prevent row click
            onRowClick?.(row.original); // Navigate to market
          };
          
          return (
            <span 
              className="font-medium cursor-pointer hover:text-primary hover:underline transition-colors"
              onClick={handleSymbolClick}
            >
              {row.original.symbol}
            </span>
          );
        },
      },
      {
        id: "side",
        accessorKey: "side",
        header: (props) => <HeaderCell header={props}>Side</HeaderCell>,
        cell: ({ row }) => {
          const side = row.original.side;
          const isLong = side === "long" || side === "buy";
          return (
            <span
              className={cn(
                "capitalize font-medium",
                isLong ? "text-buy" : "text-sell"
              )}
            >
              {side === "buy" ? "long" : side === "sell" ? "short" : side}
            </span>
          );
        },
      },
      {
        id: "type",
        accessorKey: "type",
        header: (props) => <HeaderCell header={props}>Type</HeaderCell>,
        cell: ({ row }) => (
          <span className="capitalize">{row.original.type}</span>
        ),
      },
      {
        id: "orderValue",
        header: (props) => <HeaderCell header={props} align="right">Order Value</HeaderCell>,
        cell: ({ row }) => {
          // Use notional_value if available (user entered USD), otherwise calculate from quantity × limitPrice
          // This preserves the exact USD value the user intended (e.g., $5,000 instead of $4,828.66)
          const { quantity, limitPrice, notionalValue } = row.original;
          const orderValue = notionalValue ?? (limitPrice ? quantity * limitPrice : 0);
          
          return (
            <div className="text-right font-medium">
              {orderValue > 0 ? `$${formatCurrency(orderValue, 2)}` : "-"}
            </div>
          );
        },
      },
      {
        id: "quantity",
        accessorKey: "quantity",
        header: (props) => <HeaderCell header={props} align="right">Quantity</HeaderCell>,
        cell: ({ row }) => (
          <div className="text-right">
            {formatCurrency(row.original.quantity, row.original.base.precision)}{" "}
            <span className="text-muted-foreground text-xs">{row.original.base.code}</span>
          </div>
        ),
      },
      {
        id: "limitPrice",
        accessorKey: "limitPrice",
        header: (props) => <HeaderCell header={props} align="right">Limit Price</HeaderCell>,
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.limitPrice
              ? `$${formatCurrency(row.original.limitPrice, row.original.quote.precision)}`
              : "Market"}
          </div>
        ),
      },
      {
        id: "currentPrice",
        accessorKey: "currentPrice",
        header: (props) => <HeaderCell header={props} align="right">Current Price</HeaderCell>,
        cell: ({ row }) => (
          <div className="text-right">
            ${formatCurrency(row.original.currentPrice, row.original.quote.precision)}
          </div>
        ),
      },
      {
        id: "progress",
        accessorKey: "progressPercent",
        header: (props) => <HeaderCell header={props} align="right">Filled</HeaderCell>,
        cell: ({ row }) => (
          <div className="text-right">
            {(row.original.progressPercent * 100).toFixed(1)}%
          </div>
        ),
      },
      {
        id: "tpsl",
        header: (props) => <HeaderCell header={props} align="right">TP / SL</HeaderCell>,
        cell: ({ row }) => {
          const { takeProfit, stopLoss } = row.original;
          const hasTP = takeProfit !== undefined && takeProfit !== null && takeProfit > 0;
          const hasSL = stopLoss !== undefined && stopLoss !== null && stopLoss > 0;
          const hasTpSl = hasTP || hasSL;
          
          if (!hasTpSl) {
            return <div className="text-right text-muted-foreground">-</div>;
          }
          
          return (
            <div className="text-right flex items-center justify-end">
              <div className="flex flex-col items-end gap-0">
                {hasTP && (
                  <div className="flex items-center gap-1">
                    <span className="text-buy text-[11px] font-medium">
                      TP: ${formatCurrency(takeProfit!, 2)}
                    </span>
                    {onRemoveTakeProfit && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveTakeProfit(row.original);
                        }}
                        className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
                        title="Remove Take Profit"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
                {hasSL && (
                  <div className="flex items-center gap-1">
                    <span className="text-sell text-[11px] font-medium">
                      SL: ${formatCurrency(stopLoss!, 2)}
                    </span>
                    {onRemoveStopLoss && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveStopLoss(row.original);
                        }}
                        className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
                        title="Remove Stop Loss"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => <div className="text-right pr-2">Actions</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onCancelOrder?.(row.original.id);
              }}
              disabled={isCancelling === row.original.id}
              title="Cancel order"
            >
              {isCancelling === row.original.id ? (
                <span className="text-xs">...</span>
              ) : (
                <X className="h-4 w-4" />
              )}
            </Button>
          </div>
        ),
      },
    ],
    [onCancelOrder, isCancelling, onRemoveTakeProfit, onRemoveStopLoss]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: (updater) => {
      const newSorting = typeof updater === "function" ? updater(sorting) : updater;
      onSort(newSorting);
    },
    state: {
      sorting,
    },
    manualSorting: true,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-background">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={cn(
                "border-t border-border/40 transition-colors",
                editingOrderId === row.original.id && "opacity-50 pointer-events-none", // Disabled while editing
                row.original.progressPercent === 0 
                  ? "cursor-pointer hover:bg-muted/50" 
                  : "cursor-not-allowed opacity-60"
              )}
              onClick={() => {
                if (row.original.progressPercent > 0) {
                  toast.info("Cannot edit partially filled orders");
                  return;
                }
                onEditOrder?.(row.original);
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-2 whitespace-nowrap">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});
